import { Capacitor } from "@capacitor/core";
import { useQuery } from "@tanstack/react-query";
import { ExtractRouteParams, RouteComponentProps } from "react-router";
import { useFeatureFlags } from "swing-components";

import { ContentSingleColumn } from "~components";
import { InvalidConfiguration, OnboardingPage, WebOnlyConfiguration } from "~onboarding/components";
import { ONBOARDING_ROUTES } from "~onboarding/index";
import { getConfiguration } from "~onboarding/utils";

import { RejectionPageBase } from "./RejectionPageBase";

type RejectionPageProps = RouteComponentProps<
  ExtractRouteParams<(typeof ONBOARDING_ROUTES)["rejection"], string>
>;

/***** Component *****/
export function RejectionPage(props: RejectionPageProps) {
  const { match } = props;
  const { onboardingConfigurationId } = match.params;

  /***** Hooks *****/
  const { flags } = useFeatureFlags();
  const { data: configuration, isError: isConfigurationError } = useQuery({
    queryKey: [
      "configuration",
      onboardingConfigurationId,
      onboardingConfigurationId === "ca-v4" ? flags?.onboardingTask : undefined,
      onboardingConfigurationId === "nj" ? flags?.onboardingProfileRequired : undefined,
    ],
    queryFn: () => getConfiguration(onboardingConfigurationId, { featureFlags: flags }),
    // Cache the configuration for the lifetime of the application since this
    // only changes on new deployments which will require an application refresh.
    staleTime: Infinity,
    // Disabling retries since the query is a lazy file import load, thus if
    // there is an error, it's on purpose.
    retry: false,
  });

  /*** Render #1 - Mobile Device ***/
  if (Capacitor.isNativePlatform()) {
    return (
      <OnboardingPage>
        <ContentSingleColumn>
          <WebOnlyConfiguration />
        </ContentSingleColumn>
      </OnboardingPage>
    );
  }

  /*** Render #2 - Invalidate Configuration ***/
  if (isConfigurationError === true) {
    return (
      <OnboardingPage>
        <ContentSingleColumn>
          <InvalidConfiguration />
        </ContentSingleColumn>
      </OnboardingPage>
    );
  }

  /*** Render #3 - Loading ***/
  if (!configuration) return <OnboardingPage isLoading={true} />;

  /*** Render #4 ***/
  return (
    <OnboardingPage>
      <ContentSingleColumn>
        <RejectionPageBase rejectionMessageMarkdown={configuration.rejectionMessageMarkdown} />
      </ContentSingleColumn>
    </OnboardingPage>
  );
}
